import { api } from './client';
import {
  ConversionDiagnostics,
  ConversionReport,
  ReportListResponse,
  ReportStatus,
} from '../types';

export interface RefundReportResponse {
  report: ConversionReport;
  newBalance: number;
}

/**
 * Admin review inbox. Every call requires the signed-in user to be on the
 * backend's ADMIN_EMAILS allowlist; the API answers 403 otherwise.
 */
export const adminApi = {
  listReports: async (
    status?: ReportStatus,
    limit: number = 50,
    offset: number = 0
  ): Promise<ReportListResponse> => {
    return api.get<ReportListResponse>('/v1/admin/reports', {
      status,
      limit,
      offset,
    });
  },

  getReport: async (id: string): Promise<ConversionReport> => {
    return api.get<ConversionReport>(`/v1/admin/reports/${id}`);
  },

  updateReport: async (
    id: string,
    changes: { status?: ReportStatus; adminNotes?: string }
  ): Promise<ConversionReport> => {
    return api.patch<ConversionReport>(`/v1/admin/reports/${id}`, changes);
  },

  /** Create (or return the existing) GitHub issue for a report. */
  createIssue: async (id: string): Promise<ConversionReport> => {
    return api.post<ConversionReport>(`/v1/admin/reports/${id}/issue`);
  },

  /** Refund one credit to the reporter. The API refuses to refund twice. */
  refundReport: async (id: string): Promise<RefundReportResponse> => {
    return api.post<RefundReportResponse>(`/v1/admin/reports/${id}/refund`);
  },

  /** What each pipeline stage recorded for the reported conversion. */
  getDiagnostics: async (id: string): Promise<ConversionDiagnostics> => {
    return api.get<ConversionDiagnostics>(`/v1/admin/reports/${id}/diagnostics`);
  },

  /** Signed URL so an admin can listen to the reported audio. */
  getAudioUrl: async (id: string): Promise<{ audioUrl: string; conversionId: string }> => {
    return api.get<{ audioUrl: string; conversionId: string }>(
      `/v1/admin/reports/${id}/audio`
    );
  },
};

export default adminApi;
